/**
 * SGX Module - Singapore Exchange Sustainability Reporting
 * 5 ESG aspects with 27+ core and advanced metrics, TCFD-aligned
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Save, RefreshCw, Download, FileText, Sparkles,
  Leaf, Users, Shield, DollarSign, Cloud, Globe
} from 'lucide-react';
import { useAlert } from '../../context/AlertContext';

const API = process.env.REACT_APP_BACKEND_URL;

const SGXModule = ({ study }) => {
  const { showSuccess, showError, showWarning, showInfo, confirm } = useAlert();
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [metricValues, setMetricValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generatingNarrative, setGeneratingNarrative] = useState(false);
  const [narratives, setNarratives] = useState({});
  const [reportingPeriod, setReportingPeriod] = useState('');
  const [filterType, setFilterType] = useState('all'); // all, core, advanced, tcfd

  useEffect(() => {
    if (study?.id) {
      fetchData();
    }
  }, [study?.id]);

  const getCurrentFY = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const startYear = month >= 3 ? year : year - 1;
    return `FY ${startYear}-${String(startYear + 1).slice(-2)}`;
  };

  const fetchData = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      const [response, metadataRes] = await Promise.all([
        axios.get(`${API}/api/sgx/${study.id}/categories`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/metadata/${study.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { metadata: {} } }))
      ]);

      const meta = metadataRes.data?.metadata || metadataRes.data || {};
      setReportingPeriod(meta.financial_year || getCurrentFY());

      setCategories(response.data.categories || []);

      // Initialize metric values from saved data
      const values = {};
      response.data.categories?.forEach(category => {
        category.metrics?.forEach(metric => {
          if (metric.value !== undefined) {
            values[metric.id] = {
              value: metric.value,
              notes: metric.notes || ''
            };
          }
        });
      });
      setMetricValues(values);

      // Set first category as active
      if (response.data.categories?.length > 0) {
        setActiveCategory(response.data.categories[0].id);
      }
    } catch (error) {
      console.error('Error fetching SGX data:', error);
      showError('Failed to load SGX data');
    } finally {
      setLoading(false);
    }
  };

  const handleValueChange = (metricId, field, value) => {
    setMetricValues(prev => ({
      ...prev,
      [metricId]: {
        ...prev[metricId],
        [field]: value
      }
    }));
  };

  const saveCategoryData = async (categoryId) => {
    setSaving(true);
    const token = localStorage.getItem('token');

    // Collect all metrics across all categories
    const allMetrics = [];
    categories.forEach(category => {
      category.metrics.forEach(met => {
        if (metricValues[met.id]?.value) {
          allMetrics.push({
            category_id: category.id,
            metric_id: met.metric_id,
            value: metricValues[met.id]?.value || null,
            unit: met.unit || "",
            remarks: metricValues[met.id]?.notes || null
          });
        }
      });
    });

    try {
      await axios.post(
        `${API}/api/sgx/${study.id}/data`,
        { metrics: allMetrics },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccess('Data saved successfully!');
    } catch (error) {
      console.error('Error saving data:', error);
      showError('Failed to save data: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const generateAIPoweredReport = async () => {
    setGeneratingNarrative(true);
    const token = localStorage.getItem('token');

    try {
      // First generate narratives for all categories
      showInfo('Generating AI-powered report with narratives... This may take a few minutes.');

      await axios.post(
        `${API}/api/sgx/${study.id}/generate-narratives`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Then download the PDF with narratives
      const response = await axios.get(
        `${API}/api/sgx/${study.id}/export/pdf?token=${token}`,
        {
          responseType: 'blob',
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `SGX_Report_${study.name || 'Report'}_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showSuccess('AI-powered report generated and downloaded successfully!');
    } catch (error) {
      console.error('Error generating report:', error);
      showError(error.response?.data?.detail || 'Failed to generate AI-powered report');
    } finally {
      setGeneratingNarrative(false);
    }
  };

  const exportPDF = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(
        `${API}/api/sgx/${study.id}/export/pdf?reporting_period=${encodeURIComponent(reportingPeriod)}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `sgx_report_${study.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting PDF:', error);
      showError('Failed to export PDF');
    }
  };

  const exportExcel = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(
        `${API}/api/sgx/${study.id}/export/excel`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `sgx_report_${study.id}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting Excel:', error);
      showError('Failed to export Excel');
    }
  };

  const getCategoryIcon = (categoryId) => {
    const icons = {
      env_climate: Cloud,
      env_resources: Leaf,
      social_workforce: Users,
      social_community: Users,
      governance: Shield
    };
    return icons[categoryId] || Leaf;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-green-500" />
        <span className="ml-3 text-lg">Loading SGX Module...</span>
      </div>
    );
  }

  const currentCategory = categories.find(c => c.id === activeCategory);

  // Filter metrics based on selected filter
  const getFilteredMetrics = (metrics) => {
    if (filterType === 'all') return metrics;
    if (filterType === 'core') return metrics.filter(m => m.type === 'core');
    if (filterType === 'advanced') return metrics.filter(m => m.type === 'advanced');
    if (filterType === 'tcfd') return metrics.filter(m => m.tcfd);
    return metrics;
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-teal-600 rounded-lg shadow-lg p-6 mb-6 text-white">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold mb-2">
              SGX Sustainability Reporting
            </h1>
            <p className="text-teal-100">
              Singapore Exchange ESG Framework • {study?.study_name}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={exportExcel}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <Download className="w-4 h-4" />
              Excel
            </button>
            <button
              onClick={generateAIPoweredReport}
              disabled={generatingNarrative}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              {generatingNarrative ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4" />
              )}
              AI-Powered Report
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-6 pt-4 border-t border-white/20 flex gap-6 items-center">
          <div>
            <label className="block text-sm font-semibold text-white mb-2">
              Filter Metrics
            </label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border-2 border-white/30 bg-white/10 text-white rounded-lg focus:outline-none focus:border-white focus:bg-white/20"
            >
              <option value="all">All Metrics</option>
              <option value="core">Core Only</option>
              <option value="advanced">Advanced Only</option>
              <option value="tcfd">TCFD-Aligned Only</option>
            </select>
          </div>
        </div>
      </div>

      <div className="flex gap-6">
        {/* Sidebar - Categories */}
        <div className="w-80 bg-white rounded-lg shadow-md p-4">
          <h2 className="text-lg font-semibold mb-4 text-gray-800">5 ESG Categories</h2>
          <div className="space-y-2">
            {categories.map((category) => {
              const Icon = getCategoryIcon(category.id);
              return (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`w-full text-left p-3 rounded-lg transition-all flex items-center gap-3 ${
                    activeCategory === category.id
                      ? 'text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  style={
                    activeCategory === category.id
                      ? { backgroundColor: category.color }
                      : {}
                  }
                >
                  <Icon className="w-5 h-5" />
                  <div className="flex-1">
                    <div className="font-medium text-sm">{category.title}</div>
                    <div className="text-xs opacity-80">
                      {category.aspect}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Main Content - Active Category */}
        <div className="flex-1 bg-white rounded-lg shadow-md p-6">
          {currentCategory && (
            <>
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-2 flex items-center gap-3">
                  <div
                    className="w-3 h-8 rounded"
                    style={{ backgroundColor: currentCategory.color }}
                  />
                  {currentCategory.title}
                </h2>
              </div>

              {/* Metrics */}
              <div className="space-y-6">
                <div className="flex justify-between items-center border-b pb-2">
                  <h3 className="text-lg font-semibold text-gray-800">Metrics</h3>
                  <span className="text-sm text-gray-600">
                    Showing {getFilteredMetrics(currentCategory.metrics).length} of {currentCategory.metrics.length}
                  </span>
                </div>

                {getFilteredMetrics(currentCategory.metrics).map((metric) => (
                  <div key={metric.id} className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <label className="block font-medium text-gray-800">
                        {metric.name}
                      </label>
                      <div className="flex gap-2">
                        <span className={`text-xs px-2 py-1 rounded ${
                          metric.type === 'core'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-purple-100 text-purple-800'
                        }`}>
                          {metric.type.toUpperCase()}
                        </span>
                        {metric.tcfd && (
                          <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-800">
                            TCFD
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mt-3">
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">
                          Value ({metric.unit})
                        </label>
                        <input
                          type="text"
                          value={metricValues[metric.id]?.value || ''}
                          onChange={(e) => handleValueChange(metric.id, 'value', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          placeholder={`Enter ${metric.unit}`}
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">
                          Notes
                        </label>
                        <input
                          type="text"
                          value={metricValues[metric.id]?.notes || ''}
                          onChange={(e) => handleValueChange(metric.id, 'notes', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          placeholder="Additional notes"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Actions */}
              <div className="mt-6 flex gap-3 border-t pt-6">
                <button
                  onClick={() => saveCategoryData(currentCategory.id)}
                  disabled={saving}
                  className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Save Data
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SGXModule;
